import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { useActivityLog } from '@/composables/useActivityLog'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import type { Organization } from '@/types'
import type { OrganizationsState } from './state'

export interface CreateOrganizationData {
  name: string
  email?: string
  phone?: string
  address?: string
  website?: string
  notes?: string
  ownerId?: string
}

export interface UpdateOrganizationData {
  name?: string
  email?: string
  phone?: string
  address?: string
  website?: string
  notes?: string
  isActive?: boolean
  ownerId?: string
}

export const createActions = (state: OrganizationsState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  let globalUnsubscribe: Unsubscribe | null = null

  const mapOrgDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): Organization => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      website: data.website,
      notes: data.notes,
      ownerId: data.ownerId,
      memberIds: (data.memberIds as string[]) || [],
      isActive: (data.isActive as boolean) ?? true,
      createdAt: (data.createdAt as { toDate: () => Date })?.toDate() || new Date(),
      updatedAt: (data.updatedAt as { toDate: () => Date })?.toDate() || new Date(),
      createdBy: data.createdBy,
      updatedBy: data.updatedBy,
      tenantId: data.tenantId as string | undefined,
    } as Organization
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    // Operators use batch fetch by IDs — not suitable for onSnapshot
    // Use global query and filter client-side
    const q = query(collection(db, 'organizations'), ...baseConstraints, orderBy('createdAt', 'desc'))
    state.isLoading = true

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      let orgs = snapshot.docs.map(mapOrgDoc)

      // Client-side filter for operators
      if (authStore.user?.role === 'operator') {
        const orgIds = new Set(authStore.user.assignedOrganizationIds || [])
        orgs = orgs.filter((o) => orgIds.has(o.id))
      }

      state.organizations = orgs
      state.isLoading = false
    }, (error) => {
      console.error('Organizations subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchOrganizations = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      if (authStore.user?.role === 'operator') {
        const orgIds = authStore.user.assignedOrganizationIds || []
        if (orgIds.length === 0) {
          state.organizations = []
          return
        }
        // Fetch por document IDs (batches de 30)
        const allOrgs: Organization[] = []
        for (let i = 0; i < orgIds.length; i += 30) {
          const batch = orgIds.slice(i, i + 30)
          const constraints = [...baseConstraints, where('__name__', 'in', batch)]
          const q = query(collection(db, 'organizations'), ...constraints)
          const snap = await getDocs(q)
          allOrgs.push(...snap.docs.map(mapOrgDoc))
        }
        state.organizations = allOrgs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        return
      }

      const q = query(collection(db, 'organizations'), ...baseConstraints, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      state.organizations = snapshot.docs.map(mapOrgDoc)
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar organizaciones'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchOrganizationById = async (orgId: string): Promise<Organization | null> => {
    try {
      const docRef = doc(db, 'organizations', orgId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      const organization: Organization = {
        id: docSnap.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        website: data.website,
        notes: data.notes,
        ownerId: data.ownerId,
        memberIds: data.memberIds || [],
        isActive: data.isActive ?? true,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
        tenantId: data.tenantId,
      }

      state.selectedOrganization = organization
      return organization
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar organizacion'
      throw error
    }
  }

  const createOrganization = async (data: CreateOrganizationData): Promise<Organization> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      const newOrg = {
        ...getTenantData(),
        name: data.name,
        email: data.email || null,
        phone: data.phone || null,
        address: data.address || null,
        website: data.website || null,
        notes: data.notes || null,
        ownerId: data.ownerId || null,
        memberIds: data.ownerId ? [data.ownerId] : [],
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      const docRef = await addDoc(collection(db, 'organizations'), newOrg)

      const createdOrg: Organization = {
        id: docRef.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        website: data.website,
        notes: data.notes,
        ownerId: data.ownerId,
        memberIds: data.ownerId ? [data.ownerId] : [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      // Update owner's user document with organizationId
      if (data.ownerId) {
        await updateDoc(doc(db, 'users', data.ownerId), {
          organizationId: docRef.id,
          isOrganizationOwner: true,
          updatedAt: serverTimestamp(),
          updatedBy: currentUserId,
        })
      }

      state.organizations.unshift(createdOrg)
      await logCreate('organization', createdOrg.id, createdOrg.name)

      return createdOrg
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al crear organizacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateOrganization = async (orgId: string, data: UpdateOrganizationData): Promise<Organization> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      const currentOrg = state.organizations.find((o) => o.id === orgId)
      if (!currentOrg) {
        throw new Error('Organizacion no encontrada')
      }

      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      }

      await updateDoc(doc(db, 'organizations', orgId), updateData)

      // Handle owner change: update user documents
      if (data.ownerId && data.ownerId !== currentOrg.ownerId) {
        // Remove owner flag from previous owner
        if (currentOrg.ownerId) {
          await updateDoc(doc(db, 'users', currentOrg.ownerId), {
            isOrganizationOwner: false,
            updatedAt: serverTimestamp(),
            updatedBy: currentUserId,
          })
        }
        // Set new owner and ensure they are a member
        await updateDoc(doc(db, 'users', data.ownerId), {
          organizationId: orgId,
          isOrganizationOwner: true,
          updatedAt: serverTimestamp(),
          updatedBy: currentUserId,
        })
        // Add to memberIds if not already
        if (!currentOrg.memberIds.includes(data.ownerId)) {
          await updateDoc(doc(db, 'organizations', orgId), {
            memberIds: arrayUnion(data.ownerId),
          })
          currentOrg.memberIds.push(data.ownerId)
        }
      }

      const changes = detectChanges(
        currentOrg as unknown as Record<string, unknown>,
        data as Partial<Record<string, unknown>>,
        ['name', 'email', 'phone', 'address', 'website', 'isActive', 'ownerId']
      )

      const updatedOrg: Organization = {
        ...currentOrg,
        ...data,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }

      const index = state.organizations.findIndex((o) => o.id === orgId)
      if (index !== -1) {
        state.organizations[index] = updatedOrg
      }

      if (state.selectedOrganization?.id === orgId) {
        state.selectedOrganization = updatedOrg
      }

      if (changes.length > 0) {
        await logUpdate('organization', orgId, changes, updatedOrg.name)
      }

      return updatedOrg
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al actualizar organizacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteOrganization = async (orgId: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const org = state.organizations.find((o) => o.id === orgId)

      await deleteDoc(doc(db, 'organizations', orgId))

      state.organizations = state.organizations.filter((o) => o.id !== orgId)

      if (org) {
        await logDelete('organization', orgId, org.name)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al eliminar organizacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const addMember = async (orgId: string, userId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      await updateDoc(doc(db, 'organizations', orgId), {
        memberIds: arrayUnion(userId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      await updateDoc(doc(db, 'users', userId), {
        organizationId: orgId,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      const org = state.organizations.find((o) => o.id === orgId)
      if (org && !org.memberIds.includes(userId)) {
        org.memberIds.push(userId)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al agregar miembro'
      throw error
    }
  }

  const removeMember = async (orgId: string, userId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      await updateDoc(doc(db, 'organizations', orgId), {
        memberIds: arrayRemove(userId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      await updateDoc(doc(db, 'users', userId), {
        organizationId: null,
        isOrganizationOwner: false,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      const org = state.organizations.find((o) => o.id === orgId)
      if (org) {
        org.memberIds = org.memberIds.filter((id) => id !== userId)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al remover miembro'
      throw error
    }
  }

  const setSelectedOrganization = (org: Organization | null): void => {
    state.selectedOrganization = org
  }

  const setFilters = (filters: Partial<OrganizationsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearError = (): void => {
    state.error = null
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchOrganizations,
    fetchOrganizationById,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    addMember,
    removeMember,
    setSelectedOrganization,
    setFilters,
    clearError,
  }
}
